import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  OutlinedInput,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import { theme } from '../../ThemeContext/ThemeObject';
import { ICandidateGroup } from '../AgenticMatching/types';

export default function SelectedCandidates({
  handleClose,
  selectedCandidateGroups,
  onRemoveCandidateGroup,
  invalidCandidateGroups
}: {
  handleClose: () => void;
  selectedCandidateGroups: ICandidateGroup[];
  onRemoveCandidateGroup: (candidateGroupId: number) => void;
  invalidCandidateGroups: ICandidateGroup[];
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const filterCandidates = (candidateGroup: ICandidateGroup) => {
    return searchTerm
      ? `${candidateGroup.name?.toLowerCase()}`.includes(searchTerm.toLowerCase()) ||
          candidateGroup.emails.some((email) => email.includes(searchTerm)) ||
          candidateGroup.phones.some((phone) => phone.includes(searchTerm))
      : true;
  };

  const handleRemoveCandidateGroup = (candidateGroupId: number) => {
    onRemoveCandidateGroup(candidateGroupId);
    if (selectedCandidateGroups?.length <= 1) handleClose();
  };

  return (
    <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
      <h3
        style={{
          color: '#939393',
          fontSize: '19px',
          fontWeight: '700',
          marginBottom: '30px'
        }}
      >
        Candidates
      </h3>
      {selectedCandidateGroups.length > 0 && (
        <>
          {selectedCandidateGroups.length > 50 ? (
            <Box></Box>
          ) : (
            <OutlinedInput
              id="candidate-search"
              sx={{
                width: '100%',
                marginBottom: '16px',
                '& fieldset': {
                  borderColor: '#939393',
                  backgroundColor: '#AAAAAA09'
                },
                '&:hover': {
                  '& fieldset': {
                    borderColor: '#6DB5B1 !important'
                  }
                }
              }}
              placeholder={'Search candidates'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startAdornment={
                <InputAdornment sx={{ padding: '0px 0px 0px 10px' }} position="start">
                  <SearchIcon />
                </InputAdornment>
              }
            />
          )}
          <Box
            sx={{
              fontFamily: 'Source Sans Pro',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#084D6D',
              marginBottom: '8px'
            }}
          >
            <Box>{selectedCandidateGroups.length} candidates selected.</Box>
            {invalidCandidateGroups.length > 0 && (
              <Box style={{ color: '#FF0000' }}>
                {invalidCandidateGroups.length} cannot be added to the pools due to missing emails, please remove them
              </Box>
            )}
          </Box>
          {selectedCandidateGroups.length > 50 ? (
            <Box
              sx={{
                marginBottom: '20px',
                fontFamily: 'Source Sans Pro',
                fontSize: '14px',
                color: '#939393'
              }}
            >
              {selectedCandidateGroups.length} candidates will be added to the selected pools
            </Box>
          ) : (
            <TableContainer sx={{ boxShadow: 'none', marginBottom: '20px' }} component={Paper}>
              <Table
                sx={{
                  minWidth: '700px',
                  'td, th, tr, thead': {
                    fontFamily: 'Source Sans Pro, sans-serif',
                    border: 'none',
                    backgroundColor: '#FFFFFF',
                    textTransform: 'none',
                    padding: '5px 8px 10px 0px'
                  },
                  th: {
                    color: '#084D6D',
                    fontWeight: 'bold',
                    paddingBottom: '16px'
                  },
                  tr: {
                    fontSize: '16px',
                    borderBottom: '1px solid #DDDDDD'
                  }
                }}
                aria-label="selected candidates"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Emails</TableCell>
                    <TableCell>Phones</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCandidateGroups
                    .filter((candidateGroup) => filterCandidates(candidateGroup))
                    .map((candidateGroup: ICandidateGroup) => {
                      const isInvalid = invalidCandidateGroups.includes(candidateGroup);
                      return (
                        <TableRow key={candidateGroup.id}>
                          <TableCell sx={{ opacity: isInvalid ? 0.5 : 1 }}>
                            <a
                              href={`/admin/candidate_sourcing/candidate_groups/${candidateGroup.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#666666',
                                fontWeight: 'bold',
                                textDecoration: 'none'
                              }}
                            >
                              {candidateGroup.name}
                            </a>
                          </TableCell>
                          <TableCell sx={{ opacity: isInvalid ? 0.5 : 1 }}>
                            {candidateGroup.emails.join(', ')}
                          </TableCell>
                          <TableCell sx={{ opacity: isInvalid ? 0.5 : 1 }}>
                            {candidateGroup.phones.join(', ')}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleRemoveCandidateGroup(candidateGroup.id)}
                              sx={{ marginLeft: '35px' }}
                            >
                              <ClearIcon sx={{ color: theme.palette.error.main }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
